import { startTransition } from 'react';
import type { MutableRefObject } from 'react';
import type { EditorScreen } from '@/store/editorSlice';
import { safeHref } from '@/utils/isAllowedUrl';
import type { TransitionResult } from '@/pages/navigation/navigationStateMachine';
import type { HistoryStack } from '@/pages/navigation/historyStack';
import type { PendingLeadSnapshot } from './quizProgressStorage';

/**
 * Исполнение исхода цепочки решения перехода (`decideTransition`) в плеере
 * квиза: переход на экран, экран результата, RESTART, уход по адресу,
 * завершение и переход к пейволлу воронки (WEB-3819).
 *
 * WEB-3819 (возврат 2 с конвейера): вынесено из QuizScreenWizard.tsx без
 * смены поведения — тело ветвления перенесено как было, плеер зовёт функцию
 * на прежнем месте `handleNext`; зависимости — состояние и двери плеера.
 */
export interface QuizTransitionDispatchDeps {
  screens: EditorScreen[];
  safeIndex: number;
  isControlled: boolean;
  historyStack: Pick<HistoryStack, 'push' | 'clearHistory'>;
  transitionInFlightRef: MutableRefObject<boolean>;
  setCurrentIndex: (index: number) => void;
  fireQuizComplete: () => void;
  emitQuizComplete: () => void;
  leaveQuizOutward: (target: { ruleId?: string }, fallbackHref: string) => void;
  resetValues: () => void;
  setVariables: (next: Record<string, string>) => void;
  setEmailCaptured: (next: boolean) => void;
  setEmailDelivered: (next: boolean) => void;
  rememberPendingLead: (next: PendingLeadSnapshot | null) => void;
  setAutoAdvancedBlocks: (next: string[]) => void;
}

export function dispatchQuizTransition(
  transition: TransitionResult,
  {
    screens,
    safeIndex,
    isControlled,
    historyStack,
    transitionInFlightRef,
    setCurrentIndex,
    fireQuizComplete,
    emitQuizComplete,
    leaveQuizOutward,
    resetValues,
    setVariables,
    setEmailCaptured,
    setEmailDelivered,
    rememberPendingLead,
    setAutoAdvancedBlocks,
  }: QuizTransitionDispatchDeps,
): void {
  // F1b: dispatch the action.
  // WEB-848: every setCurrentIndex call below is additionally gated on
  // `!isControlled` (Вариант B) — history/cycle-guard bookkeeping still
  // runs so the chrome stays "live", it just does not move the preview
  // off the screen the editor has selected.
  switch (transition.type) {
    case 'GOTO_SCREEN': {
      const targetIdx = screens.findIndex((s) => s.id === transition.targetId);
      if (targetIdx >= 0) {
        historyStack.push(safeIndex);
        // WEB-1993 п.3: смена экрана — ПЕРЕХОД, а не срочная перерисовка.
        // Срочное здесь одно: подсветка нажатого варианта и погасшая
        // кнопка, они обязаны попасть в ближайший кадр. Сборка нового
        // экрана тяжелее и уходит отдельной, прерываемой работой — именно
        // её длина и мерится как INP. Поведение не меняется: React лишь
        // вправе показать кадр раньше, порядок состояний тот же.
        if (!isControlled) {
          // Защёлку взводим только при РЕАЛЬНОЙ смене индекса: правило
          // «перейти к себе» не меняет currentIndex, эффект снятия не
          // сработает — и кнопка «Далее» осталась бы мёртвой (приёмка
          // починки, проход 4 свежего взгляда 29).
          if (targetIdx !== safeIndex) transitionInFlightRef.current = true;
          startTransition(() => setCurrentIndex(targetIdx));
        }
      }
      break;
    }
    case 'GOTO_ENDING': {
      // WEB-189 (§172): render the RESOLVED ending screen in full (the client's
      // custom result_screen / explicit target / last-screen fallback). Per
      // §172 the generic "Thank you" view is a fallback ONLY when there are no
      // screens at all — и handleNext не запускается без текущего экрана.
      // WEB-1717 сузил «всегда переходим» до «переходим, когда экран
      // результата ДРУГОЙ»: цель на самого себя — это завершение (ниже).
      const endingIdx = screens.findIndex((s) => s.id === transition.targetId);
      // WEB-1717 (Urgent, про деньги): экран результата = ТЕКУЩИЙ экран (или
      // цель вообще не разрешилась). Раньше здесь писалась история и ставился
      // ТОТ ЖЕ индекс — визуально не происходило ничего: покупатель жал кнопку
      // снова и снова, пока не срабатывал аварийный сторож зацикливания
      // (max(экранов*3, 30) нажатий), и только он завершал квиз. Экрана оплаты
      // покупатель не видел вообще, а событие завершения уходило с первого
      // нажатия — воронка считала его дошедшим. Идти дальше некуда: это конец
      // квиза, честно завершаем его С ПОКАЗОМ того, что положено (пейволл
      // воронки, иначе заглушка). Историю при этом НЕ пишем — фиктивный
      // переход «сам в себя» не должен ни ложиться под кнопку «Назад», ни
      // крутить счётчик сторожа.
      if (endingIdx < 0 || endingIdx === safeIndex) {
        fireQuizComplete();
        break;
      }
      emitQuizComplete();
      historyStack.push(safeIndex);
      // WEB-1993 п.3: тот же переход, что и у GOTO_SCREEN выше.
      if (!isControlled) {
        transitionInFlightRef.current = true;
        startTransition(() => setCurrentIndex(endingIdx));
      }
      break;
    }
    case 'RESTART': {
      // D5 RATIFIED: RESTART resets ANSWER + VARIABLE. history cleared.
      // transitions counter is NOT reset (anti-pattern #6 prevention).
      //
      // push(currentIndex) FIRST — increments the transitions counter.
      // RESTART is a forward transition and must count toward cycle-guard.
      // Without this, a RESTART loop (A→RESTART→A→RESTART→…) would never
      // trip the guard (anti-pattern #6: counter must survive RESTART).
      // Then clearHistory() empties history WITHOUT resetting the counter.
      historyStack.push(safeIndex); // increments transitions counter
      historyStack.clearHistory(); // clears history; does NOT reset counter
      if (!isControlled) setCurrentIndex(0);
      resetValues(); // clears ANSWER (FormValuesContext)
      setVariables({}); // clears VARIABLE (F1b local state)
      setEmailCaptured(false);
      // WEB-2177 (находка 4): RESTART сбрасывает и подтверждение, и
      // недоотправку — иначе снимок нового прохождения нёс бы лид прошлого.
      setEmailDelivered(false);
      rememberPendingLead(null);
      // НАХОДКА 2 ВТОРОГО ПРОХОДА СВЕЖЕГО ВЗГЛЯДА (18.09): отметки
      // сработавшего авто-перехода — тоже состояние ПРОЙДЕННОГО прохода, и
      // RESTART обязан снимать их вместе с ответами. Иначе экран с выбором
      // варианта становится тупиком: тот же вариант больше не уносит, а
      // запасная кнопка (WEB-1794) не включается — узел авто-перехода
      // выходит раньше попытки перехода. Пока отметки жили в памяти вкладки,
      // из тупика выводила перезагрузка; сохранив их в снимок, мы закрыли и
      // этот выход.
      //
      // ⚠ ВТОРОЙ КОНЕЦ ТОЙ ЖЕ ДЫРЫ ЗДЕСЬ НЕ ЗАКРЫТ И ЭТО ИЗВЕСТНО: сам
      // `autoAdvancedRef` внутри useAutoAdvance.ts при RESTART не чистится,
      // поэтому В ТОЙ ЖЕ вкладке повторный выбор того же варианта
      // по-прежнему не уносит вперёд. Это предсуществующее поведение, не
      // регрессия этой правки; заведено отдельной карточкой координатором
      // 18.09. Здесь снимается ровно то, что добавили мы, — переживающая
      // перезагрузку половина.
      setAutoAdvancedBlocks([]);
      break;
    }
    case 'REDIRECT_URL': {
      // WEB-173 F9: scheme-allowlist guard before navigating. A disallowed
      // scheme (javascript:/data:/...) from a pre-F9 record is a no-op (the
      // write guard now blocks new ones); render-guard is defense-in-depth.
      //
      // WEB-1911 (свежий взгляд по пачкам 18/19): дверь тут та же, что у
      // остальных адресных веток плеера, — `safeHref`, а не общий предикат
      // `isAllowedUrl`. Разница ровно в маркере `w2a://store`: allowlist
      // пропускает его как допустимое ЗНАЧЕНИЕ поля (схема правил проверяет
      // цель тем же предикатом, `rule.schema.ts`), но адресом он быть не
      // может — подменять маркер настоящим адресом умеет одна ветка
      // `button_link`. Пустое запасное значение = перехода нет.
      const target = safeHref(transition.url, '');
      if (target !== '') {
        // WEB-2124: уход по адресу — ЭТО КОНЦОВКА КВИЗА, и до этой правки
        // она единственная из четырёх не засчитывалась: в ветке стояли
        // только досыл очереди и переход. У клиента с собственной страницей
        // оплаты это обнуляло главную метрику продукта («завершили — ноль»),
        // а заодно молчали повод email-цепочки и вебхук `quiz.completed`.
        //
        // Зовём `emitQuizComplete`, а не `fireQuizComplete`: второй ещё и
        // показывает заглушку «Спасибо», а мы в этот же миг уводим человека
        // — мелькание чужого экрана перед переходом покупателю не нужно.
        // Защита от повтора внутри самой функции, поэтому уход с любого
        // экрана (рекомендация карточки) задвоить событие не может.
        //
        // ПОРЯДОК СТРОК КРИТИЧЕН: событие ложится в очередь ДО досыла
        // маяком, иначе маяк уедет без него и завершение потеряется вместе
        // с выгрузкой страницы. Сам порядок живёт в ОДНОЙ двери
        // (`leaveQuizOutward`) — он же нужен блоку «редирект», и второй
        // копией расходится.
        //
        // WEB-2164: уходим НА НАШУ ТОЧКУ, а не напрямую к клиенту, — сервер
        // сам достанет адрес по id правила и посчитает шаг «ушёл на
        // оплату». `target` остаётся ЗАПАСНЫМ путём (превью автора, канва).
        leaveQuizOutward({ ruleId: transition.ruleId }, target);
      }
      break;
    }
    // WEB-3819: «перейти к пейволлу воронки» — конец квиза с этого экрана,
    // РОВНО как COMPLETE (решение владельца 23.10): «Прошли квиз» один раз,
    // пейволл воронки (A/B, предпросмотр — resolveFunnelContext), метка
    // ?s=paywall и след от пересланной ссылки, без пейволла — «Спасибо»,
    // предпросмотр в редакторе не уводит. Ничего сверх — рисковый путь.
    case 'PAYWALL':
    case 'COMPLETE': {
      fireQuizComplete();
      break;
    }
  }
}
